import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Grid,
  IconButton,
  Stack,
  TextField,
  Typography,
  Checkbox,
  FormControlLabel,
  FormGroup,
} from '@mui/material';
import Slider from '@mui/material/Slider';
import SentimentSatisfiedAltIcon from '@mui/icons-material/SentimentSatisfiedAlt';
import SentimentNeutralIcon from '@mui/icons-material/SentimentNeutral';
import SentimentVeryDissatisfiedIcon from '@mui/icons-material/SentimentVeryDissatisfied';
import dayjs from 'dayjs';

const B = { primary: '#4357AD', dark: '#1E2A4A' };
const cardSx = { borderRadius: 4, boxShadow: '0 1px 6px rgba(0,0,0,0.04)', bgcolor: '#fff' };

const LEVEL_FIELDS = [
  { key: 'lessonsLoadLevel', label: 'Завантаженість уроками' },
  { key: 'preparationLoadLevel', label: 'Завантаженість підготовкою' },
  { key: 'studentEngagementLevel', label: 'Активність учнів на уроці' },
  { key: 'attendanceSatisfactionLevel', label: 'Відвідування занять' },
  { key: 'studentMotivationLevel', label: 'Мотивація учнів' },
  { key: 'wellbeingLevel', label: 'Моральний стан викладача' },
];

const MOOD_OPTIONS = [
  { value: 'GOOD', label: 'Добре', icon: <SentimentSatisfiedAltIcon />, color: '#22C55E', bg: '#D1FAE5' },
  { value: 'NEUTRAL', label: 'Нормально', icon: <SentimentNeutralIcon />, color: '#F59E0B', bg: '#FEF3C7' },
  { value: 'LOW', label: 'Не дуже', icon: <SentimentVeryDissatisfiedIcon />, color: '#EF4444', bg: '#FEE2E2' },
];

const sectionSx = {
  borderRadius: 4,
  px: 3,
  py: 2.5,
  bgcolor: '#fff',
  border: '1px solid #EDE9FE',
  height: '100%',
  boxShadow: '0 2px 8px rgba(67,87,173,0.06)',
};

const LevelSlider = ({ value, onChange, readOnly }) => (
  <Stack direction="row" spacing={1.5} alignItems="center">
    <Slider
      value={value ?? 50}
      disabled={readOnly}
      min={0}
      max={100}
      step={1}
      valueLabelDisplay="auto"
      valueLabelFormat={(v) => `${v}%`}
      onChange={(_, newValue) => onChange?.(newValue)}
      sx={{
        color: B.primary,
        '& .MuiSlider-thumb': { width: 18, height: 18, bgcolor: '#fff', border: `3px solid ${B.primary}` },
        '& .MuiSlider-rail': { opacity: 0.15, bgcolor: B.primary },
        '& .MuiSlider-track': { bgcolor: B.primary },
      }}
    />
    <Box sx={{ minWidth: 44, height: 28, borderRadius: 2, bgcolor: '#EDE9FE', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <Typography variant="caption" fontWeight={700} sx={{ color: B.primary }}>{value ?? 50}%</Typography>
    </Box>
  </Stack>
);

const MoodSelector = ({ value, onChange, readOnly }) => {
  if (readOnly) {
    const mood = MOOD_OPTIONS.find((o) => o.value === value);
    return mood ? (
      <Chip icon={mood.icon} label={mood.label} sx={{ bgcolor: mood.bg, color: mood.color, fontWeight: 600, '& .MuiChip-icon': { color: mood.color } }} />
    ) : <Typography variant="body2" sx={{ color: '#8891A5' }}>—</Typography>;
  }

  return (
    <Stack direction="row" spacing={1.5}>
      {MOOD_OPTIONS.map((option) => {
        const selected = option.value === value;
        return (
          <Box
            key={option.value}
            onClick={() => onChange(option.value)}
            sx={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 0.5,
              py: 1.5,
              px: 1,
              borderRadius: 3,
              minWidth: 0,
              cursor: 'pointer',
              border: selected ? `2px solid ${option.color}` : '2px solid #EDE9FE',
              bgcolor: selected ? option.bg : '#fff',
              color: selected ? option.color : '#8891A5',
              transition: 'all 0.15s',
              '&:hover': { borderColor: option.color, bgcolor: option.bg },
            }}
          >
            {option.icon}
            <Typography variant="caption" fontWeight={600} sx={{ whiteSpace: 'nowrap' }}>{option.label}</Typography>
          </Box>
        );
      })}
    </Stack>
  );
};

const WeeklyReportPanel = ({
  title = 'Тижнева звітність',
  subtitle,
  values,
  onFieldChange,
  onMoodChange,
  onAttentionChange,
  onTopStudentsChange,
  onCommentChange,
  onSubmit,
  submitting,
  readOnly = false,
  emptyStateMessage = 'Ще немає відповіді за цей тиждень',
  updatedAt,
  studentOptions = [],
}) => {
  if (!values) {
    return (
      <Card sx={cardSx}>
        <CardContent>
          <Typography variant="h6" fontWeight={700} sx={{ color: B.dark }}>{title}</Typography>
          {subtitle && <Typography variant="body2" sx={{ color: '#8891A5' }}>{subtitle}</Typography>}
          <Box sx={{ py: 4, textAlign: 'center' }}>
            <Typography variant="body2" sx={{ color: '#8891A5' }}>{emptyStateMessage}</Typography>
          </Box>
        </CardContent>
      </Card>
    );
  }

  const isValid = LEVEL_FIELDS.every((field) => values[field.key] != null) && Boolean(values.weekMood);
  const selectedAttentionIds = values.attentionStudentIds || [];
  const selectedTopIds = values.topStudentIds || [];
  const studentNameMap = studentOptions.reduce((acc, s) => { acc[s.id] = s.name; return acc; }, {});

  const toggleStudent = (studentId, list, onChange) => {
    if (readOnly) return;
    const next = list.includes(studentId) ? list.filter((id) => id !== studentId) : [...list, studentId];
    onChange?.(next);
  };

  const renderStudentSelector = (selectedIds, onToggle, emptyLabel) => {
    if (readOnly) {
      return selectedIds.length ? (
        <Stack direction="row" flexWrap="wrap" gap={1}>
          {selectedIds.map((id) => (
            <Chip key={id} label={studentNameMap[id] || `ID ${id}`} sx={{ bgcolor: '#EDE9FE', color: B.primary, fontWeight: 600 }} />
          ))}
        </Stack>
      ) : <Typography variant="body2" sx={{ color: '#8891A5' }}>{emptyLabel}</Typography>;
    }
    if (!studentOptions.length) return <Typography variant="body2" sx={{ color: '#8891A5' }}>Немає доступних учнів</Typography>;
    return (
      <FormGroup sx={{ maxHeight: 200, overflowY: 'auto', border: '1px solid #EDE9FE', borderRadius: 3, px: 2, py: 1 }}>
        {studentOptions.map((student) => (
          <FormControlLabel
            key={student.id}
            control={<Checkbox checked={selectedIds.includes(student.id)} onChange={() => onToggle(student.id)} sx={{ color: '#C4B5FD', '&.Mui-checked': { color: B.primary } }} />}
            label={<Typography variant="body2" sx={{ color: B.dark }}>{student.name}</Typography>}
          />
        ))}
      </FormGroup>
    );
  };

  return (
    <Stack spacing={2.5}>
      {/* Header */}
      <Card sx={{ ...cardSx, overflow: 'hidden' }}>
        <Box sx={{ px: 3, py: 2.5, background: `linear-gradient(135deg, ${B.dark} 0%, ${B.primary} 100%)` }}>
          <Typography variant="h6" fontWeight={700} sx={{ color: '#fff' }}>{title}</Typography>
          {subtitle && <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)' }}>{subtitle}</Typography>}
          {updatedAt && <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)' }}>Оновлено: {dayjs(updatedAt).format('DD MMMM HH:mm')}</Typography>}
        </Box>
      </Card>

      {/* Sliders */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr' }, gap: 2 }}>
        {LEVEL_FIELDS.map((field) => (
          <Box key={field.key} sx={sectionSx}>
            <Typography variant="subtitle2" fontWeight={700} sx={{ color: B.dark, mb: 1.5 }}>{field.label}</Typography>
            <LevelSlider value={values[field.key]} readOnly={readOnly} onChange={(val) => onFieldChange?.(field.key, val)} />
          </Box>
        ))}
      </Box>

      {/* Bottom row: students + mood */}
      <Grid container spacing={2}>
        <Grid item xs={12} md={4}>
          <Box sx={sectionSx}>
            <Typography variant="subtitle2" fontWeight={700} sx={{ color: B.dark, mb: 0.5 }}>Потребують уваги</Typography>
            <Typography variant="caption" sx={{ color: '#8891A5', display: 'block', mb: 1.5 }}>Учні, яким потрібна підтримка</Typography>
            {renderStudentSelector(selectedAttentionIds, (id) => toggleStudent(id, selectedAttentionIds, onAttentionChange), 'Немає позначених')}
          </Box>
        </Grid>
        <Grid item xs={12} md={4}>
          <Box sx={sectionSx}>
            <Typography variant="subtitle2" fontWeight={700} sx={{ color: B.dark, mb: 0.5 }}>Топ тижня</Typography>
            <Typography variant="caption" sx={{ color: '#8891A5', display: 'block', mb: 1.5 }}>Найкращі результати</Typography>
            {renderStudentSelector(selectedTopIds, (id) => toggleStudent(id, selectedTopIds, onTopStudentsChange), 'Поки немає')}
          </Box>
        </Grid>
        <Grid item xs={12} md={4}>
          <Box sx={sectionSx}>
            <Typography variant="subtitle2" fontWeight={700} sx={{ color: B.dark, mb: 0.5 }}>Настрій тижня</Typography>
            <Typography variant="caption" sx={{ color: '#8891A5', display: 'block', mb: 1.5 }}>Як пройшов тиждень?</Typography>
            <MoodSelector value={values.weekMood} readOnly={readOnly} onChange={(val) => onMoodChange?.(val)} />
          </Box>
        </Grid>
      </Grid>

      {/* Comment */}
      <Box sx={sectionSx}>
        <Typography variant="subtitle2" fontWeight={700} sx={{ color: B.dark, mb: 1.5 }}>Коментар</Typography>
        <TextField
          placeholder="Опишіть, що було важливого цього тижня"
          multiline
          minRows={4}
          value={values.comment || ''}
          onChange={(event) => onCommentChange?.(event.target.value)}
          InputProps={{ readOnly }}
          fullWidth
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: 3,
              '& fieldset': { borderColor: '#EDE9FE' },
              '&:hover fieldset': { borderColor: B.primary },
              '&.Mui-focused fieldset': { borderColor: B.primary },
            },
          }}
        />
      </Box>

      {/* Submit */}
      {!readOnly && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Button
            variant="contained"
            onClick={onSubmit}
            disabled={!isValid || submitting}
            sx={{ bgcolor: B.primary, '&:hover': { bgcolor: '#3648A0' }, borderRadius: 3, px: 4, textTransform: 'none', fontWeight: 600 }}
          >
            {submitting ? 'Збереження...' : 'Зберегти звіт'}
          </Button>
        </Box>
      )}
    </Stack>
  );
};

export default WeeklyReportPanel;
